import React from 'react';

import { CursorProvider } from 'src/components/charts/helpers/Cursor';
import { CoordinateKind, Resolution } from 'src/lib/graphqlTypes/types';
import { SUBSCRIPTION_OPERATION_TYPE } from 'src/lib/metrics';

import { FieldMetrics } from '../useCurrentSchemaCoordinate';

import { FieldPageErrorsChart } from './fieldPageErrorsChart/FieldPageErrorsChart';
import { FieldPageLatencyDistributionChart } from './fieldPageLatencyDistributionChart/FieldPageLatencyDistributionChart';
import { FieldPageRequestsChart } from './fieldPageRequestsChart/FieldPageRequestsChart';

interface Props {
  loading: boolean;
  fieldMetrics: FieldMetrics;
  resolution: Resolution;
  showClientFiltersWarning?: boolean;
  coordinateKind: CoordinateKind;
}

export const FieldPageCharts = ({
  loading,
  fieldMetrics,
  resolution,
  showClientFiltersWarning = false,
  coordinateKind,
}: Props) => {
  // If the field metrics includes any subscription-type operation in its
  // metrics (these would be field references only), we want to show an info box
  // that explains that the execution, latency, and error counts exclude
  // subscription operations
  const showSubscriptionMissingInfo = [
    ...fieldMetrics.clientMetrics.values(),
  ].some((cm) =>
    [...cm.operationMetrics.values()].some((om) =>
      om.allOperationTypes.has(SUBSCRIPTION_OPERATION_TYPE),
    ),
  );

  const showLatenciesAndErrors =
    coordinateKind !== CoordinateKind.INPUT_OBJECT_FIELD &&
    coordinateKind !== CoordinateKind.ENUM_VALUE;

  return (
    <CursorProvider>
      <div>
        <FieldPageRequestsChart
          loading={loading}
          requests={fieldMetrics.requests}
          resolution={resolution}
          showSubscriptionMissingInfo={showSubscriptionMissingInfo}
          showClientFiltersWarning={showClientFiltersWarning}
          coordinateKind={coordinateKind}
        />
      </div>
      {showLatenciesAndErrors ? (
        <>
          <div className="pt-6">
            <FieldPageLatencyDistributionChart
              loading={loading}
              data={fieldMetrics.latencyDistribution}
              resolution={resolution}
              showSubscriptionMissingInfo={showSubscriptionMissingInfo}
              showClientFiltersWarning={showClientFiltersWarning}
            />
          </div>
          <div className="pt-6">
            <FieldPageErrorsChart
              loading={loading}
              errors={fieldMetrics?.errors}
              resolution={resolution}
              showSubscriptionMissingInfo={showSubscriptionMissingInfo}
              showClientFiltersWarning={showClientFiltersWarning}
            />
          </div>
        </>
      ) : null}
    </CursorProvider>
  );
};
